import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { Action } from "types/generated";

import { Permission } from "./types";

export const ANALYTICS = {
  analyticsPage: AnalyticsPageOrganization.OrganizationRole,
};

export const PERMISSIONS: Record<Action, Omit<Permission, "id" | "parentRole">> = {
  // V1 actions
  [Action.SpaceAdmin]: {
    name: "space:admin",
    subject: "Space",
  },
  [Action.SpaceRead]: {
    name: "space:read",
    subject: "Space",
    description:
      "Grants visibility into stacks, runs, and other resources within an assigned space",
    tooltipInfo:
      'Needed to access space-scoped resources unless custom role is combined with the system "Space Read" role.',
    tooltipInfoVariant: "standard" as const,
  },
  [Action.SpaceWrite]: {
    name: "space:write",
    subject: "Space",
  },
  [Action.RunTrigger]: {
    name: "run:trigger",
    subject: "Run",
    description: "Trigger stack runs in a given space",
  },
  [Action.RunTriggerWithCustomRuntimeConfig]: {
    name: "run:trigger with custom runtime config",
    subject: "Run",
    description: "Trigger stack runs in a given space with custom runtime config",
  },
  [Action.RunConfirm]: {
    name: "run:confirm",
    subject: "Run",
    description: "Confirm a plan and apply changes",
  },
  [Action.RunDiscard]: {
    name: "run:discard",
    subject: "Run",
    description: "Discard planned changes",
  },
  [Action.RunReview]: {
    name: "run:review",
    subject: "Run",
    description:
      "Review the planned changes and submit a review (approval/rejection). Does not replace the approval policy",
  },
  [Action.TaskCreate]: {
    name: "task:create",
    subject: "Task",
    description: "Trigger tasks for stacks in a given space",
  },
  [Action.StackManage]: {
    name: "stack:manage",
    subject: "Stack",
    description: "Manage stacks in a given space",
  },
  [Action.StackDelete]: {
    name: "stack:delete",
    subject: "Stack",
    description: "Delete stacks in a given space",
  },
  [Action.StackLock]: {
    name: "stack:lock",
    subject: "Stack",
    description: "Locks a stack for exclusive use and prevents operations by other users",
  },
  [Action.StackUnlock]: {
    name: "stack:unlock",
    subject: "Stack",
    description: "Unlocks a previously locked stack, only if locked by the same user",
  },
  [Action.StackUnlockForce]: {
    name: "stack:unlock force",
    subject: "Stack",
    description: "Unlocks any locked stack, including those locked by other users",
  },

  [Action.StackAddConfig]: {
    name: "stack:add config",
    subject: "Stack",
    description: "Adds or updates stack environment variables and mounted files",
  },
  [Action.StackDeleteConfig]: {
    name: "stack:delete config",
    subject: "Stack",
    description: "Deletes stack environment variables and mounted files",
  },
  [Action.ContextCreate]: {
    name: "context:create",
    subject: "Context",
    description: "Create contexts in a given space",
  },
  [Action.ContextUpdate]: {
    name: "context:update",
    subject: "Context",
    description: "Update contexts in a given space",
  },
  [Action.ContextDelete]: {
    name: "context:delete",
    subject: "Context",
    description: "Delete contexts in a given space",
  },

  // Actions for upcoming features

  [Action.StackCreate]: {
    name: "stack:create",
    subject: "Stack",
    description: "Create stacks in a given space",
  },
  [Action.StackUpdate]: {
    name: "stack:update",
    subject: "Stack",
    description: "Update stacks in a given space (details, source, behavior, vendor)",
  },
  [Action.StackManagedStateImport]: {
    name: "stack:managed state import",
    subject: "Stack",
    description: "Custom description",
  },
  [Action.StackReslug]: {
    name: "stack:reslug",
    subject: "Stack",
    description: "Custom description",
  },
  [Action.StackUploadLocalWorkspace]: {
    name: "stack:upload local workspace",
    subject: "Stack",
    description: "Custom description",
  },
  [Action.StackSetStar]: {
    name: "stack:star",
    subject: "Stack",
    description: "Custom description",
  },

  [Action.RunCancel]: {
    name: "run:cancel",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunCancelBlocking]: {
    name: "run:cancelBlocking",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunComment]: {
    name: "run:comment",
    subject: "Run",
    description: "Custom description",
  },

  [Action.RunPrioritizeSet]: {
    name: "run:prioritize set",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunPromote]: {
    name: "run:promote",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunProposeLocalWorkspace]: {
    name: "run:propose local workspace",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunProposeWithOverrides]: {
    name: "run:propose with overrides",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunRetry]: {
    name: "run:retry",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunRetryBlocking]: {
    name: "run:retry blocking",
    subject: "Run",
    description: "Custom description",
  },

  [Action.RunStop]: {
    name: "run:stop",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunStopBlocking]: {
    name: "run:stop blocking",
    subject: "Run",
    description: "Custom description",
  },
  [Action.RunTargetedReplan]: {
    name: "run:targeted replan",
    subject: "Run",
    description: "Custom description",
  },

  [Action.StackDisable]: {
    name: "stack:disable",
    subject: "Stack",
    description: "Custom description",
  },

  [Action.StackSetCurrentCommit]: {
    name: "stack:set current commit",
    subject: "Stack",
    description: "Custom description",
  },
  [Action.StackSyncCommit]: {
    name: "stack:sync commit",
    subject: "Stack",
    description: "Custom description",
  },
  [Action.ModuleDisable]: {
    name: "module:disable",
    subject: "Module",
    description: "Disable a module",
  },
  [Action.ModuleEnable]: {
    name: "module:enable",
    subject: "Module",
    description: "Enable a module",
  },
  [Action.ModulePublish]: {
    name: "module:publish",
    subject: "Module",
    description: "Publish a module",
  },
  [Action.StackEnable]: {
    name: "stack:enable",
    subject: "Stack",
    description: "Enable a disabled stack",
  },
  [Action.StackManagedStateRollback]: {
    name: "stack:managed state rollback",
    subject: "Stack",
    description: "Rollback managed state for a stack",
  },
};
